import React, { useEffect, useMemo, useState } from "react";
import {
	LineChart,
	Line,
	XAxis,
	YAxis,
	Tooltip,
	Legend,
	ResponsiveContainer,
	BarChart,
	Bar,
	PieChart,
	Pie,
	Cell,
	CartesianGrid,
} from "recharts";
import {
	CheckCircle2,
	Clock3,
	Loader2,
	PauseCircle,
	Users,
} from "lucide-react";
import CreateTicketModal from "../../../Modal/CreateTicketModal";
import FrameBG from "../../../../Assets/FrameBGDashboard.png";
import {
	GetUserWiseYearlyTaskReport,
	GetYearlyTaskReport,
} from "../../AdminServices/AdminReportService";
import { useTheme } from "../../../../Context/ThemeContext";

const STATUS_COLORS = {
	totalTasks: "#003a63",
	closedTasks: "#FFD166",
	openTasks: "#D1E3FF",
	inProgressTasks: "#8ECAE6",
	holdTasks: "#D1E3FF",
	underReviewTasks: "#34d399",
};

const SURFACE_COLORS = {
	primary: "#003a63",
	secondary: "#13659C",
	soft: "#BCD4E8",
	pale: "#D1E3FF",
	accent: "#FFD166",
	accentSoft: "#FFF1BF",
	teal: "#8ECAE6",
	tealDark: "#219EBC",
	border: "#D9E7F2",
};

const getCreatedCount = (row) => row?.totalTasks ?? row?.createdTasks ?? 0;
const getUnderReviewCount = (row) =>
	row?.underReviewTasks ?? row?.completedTasks ?? 0;

const AdminHome = () => {
	const { isDark } = useTheme();
	const currentYear = new Date().getFullYear();
	const [showModal, setShowModal] = useState(false);
	const [selectedYear, setSelectedYear] = useState(String(currentYear));
	const [reportView, setReportView] = useState("month");
	const [reportScope, setReportScope] = useState("overall");
	const [selectedUserId, setSelectedUserId] = useState("");
	const [yearlyReportRows, setYearlyReportRows] = useState([]);
	const [userWiseReportRows, setUserWiseReportRows] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");

	useEffect(() => {
		const fetchDashboardReports = async () => {
			try {
				setLoading(true);
				setError("");

				const [yearlyRes, userWiseRes] = await Promise.all([
					GetYearlyTaskReport({ year: selectedYear }),
					GetUserWiseYearlyTaskReport({ year: selectedYear }),
				]);

				setYearlyReportRows(yearlyRes?.data?.taskReport || []);
				setUserWiseReportRows(userWiseRes?.data?.taskReport || []);
			} catch (err) {
				console.error("Failed to fetch admin dashboard reports", err);
				setYearlyReportRows([]);
				setUserWiseReportRows([]);
				setError("Unable to load yearly report data for the dashboard.");
			} finally {
				setLoading(false);
			}
		};

		fetchDashboardReports();
	}, [selectedYear]);

	const yearOptions = Array.from({ length: 6 }, (_, index) =>
		String(currentYear - index),
	);

	const {
		overallTotals,
		monthlyTrendData,
		statusPieData,
		userOptions,
		userTotals,
		userChartData,
		selectedUserMonthlyRows,
		selectedUserMonthlyChartData,
		selectedUserTotals,
		topPerformer,
	} = useMemo(() => {
		const overallRows = Array.isArray(yearlyReportRows) ? yearlyReportRows : [];
		const userRows = Array.isArray(userWiseReportRows)
			? userWiseReportRows
			: [];
		const totals = overallRows.find((row) => row.monthNo === 13) || null;
		const monthlyRows = overallRows
			.filter((row) => row.monthNo !== 13)
			.sort((a, b) => a.monthNo - b.monthNo);

		const users = userRows
			.filter((row) => row.monthNo === 13)
			.sort((a, b) => getCreatedCount(b) - getCreatedCount(a));
		const availableUsers = users.map((row) => ({
			userId: String(row.userId),
			userName: row.userName,
		}));
		const activeUserId = selectedUserId || availableUsers[0]?.userId || "";
		const selectedUserRows = userRows
			.filter((row) => String(row.userId) === activeUserId)
			.sort((a, b) => a.monthNo - b.monthNo);
		const selectedUserTotalRow =
			selectedUserRows.find((row) => row.monthNo === 13) || null;
		const selectedUserMonthRows = selectedUserRows.filter(
			(row) => row.monthNo !== 13,
		);

		const bestUser = [...users].sort((a, b) => {
			const aCreated = getCreatedCount(a);
			const bCreated = getCreatedCount(b);
			const aRate = aCreated ? a.closedTasks / aCreated : 0;
			const bRate = bCreated ? b.closedTasks / bCreated : 0;

			return bRate - aRate || b.closedTasks - a.closedTasks;
		})[0];

		return {
			overallTotals: totals,
			monthlyTrendData: monthlyRows.map((row) => ({
				month: row.monthName,
				Created: getCreatedCount(row),
				Closed: row.closedTasks,
				"Under Review": getUnderReviewCount(row),
				Open: row.openTasks,
				"In Progress": row.inProgressTasks,
				Hold: row.holdTasks,
			})),
			statusPieData: totals
				? [
						{
							name: "Under Review",
							value: getUnderReviewCount(totals),
							color: STATUS_COLORS.underReviewTasks,
						},
						{
							name: "Closed",
							value: totals.closedTasks,
							color: STATUS_COLORS.closedTasks,
						},
						{
							name: "Open",
							value: totals.openTasks,
							color: STATUS_COLORS.openTasks,
						},
						{
							name: "In Progress",
							value: totals.inProgressTasks,
							color: STATUS_COLORS.inProgressTasks,
						},
						{
							name: "Hold",
							value: totals.holdTasks,
							color: STATUS_COLORS.holdTasks,
						},
					].filter((item) => item.value > 0)
				: [],
			userOptions: availableUsers,
			userTotals: users,
			userChartData: users.slice(0, 6).map((user) => ({
				name: user.userName,
				Created: getCreatedCount(user),
				Closed: user.closedTasks,
				"Under Review": getUnderReviewCount(user),
				Open: user.openTasks,
			})),
			selectedUserMonthlyRows: selectedUserMonthRows,
			selectedUserMonthlyChartData: selectedUserMonthRows.map((row) => ({
				month: row.monthName,
				Created: getCreatedCount(row),
				Closed: row.closedTasks,
				"Under Review": getUnderReviewCount(row),
				Open: row.openTasks,
				"In Progress": row.inProgressTasks,
				Hold: row.holdTasks,
			})),
			selectedUserTotals: selectedUserTotalRow,
			topPerformer: bestUser || null,
		};
	}, [yearlyReportRows, userWiseReportRows, selectedUserId]);

	useEffect(() => {
		if (!userOptions.length) {
			if (selectedUserId) {
				setSelectedUserId("");
			}
			return;
		}

		const hasSelectedUser = userOptions.some(
			(user) => user.userId === selectedUserId,
		);

		if (!selectedUserId || !hasSelectedUser) {
			setSelectedUserId(userOptions[0].userId);
		}
	}, [userOptions, selectedUserId]);

	const summaryCards = [
		{
			label: "Created Tasks",
			value: getCreatedCount(overallTotals),
			icon: Users,
		},
		{
			label: "Closed Tasks",
			value: overallTotals?.closedTasks ?? 0,
			icon: CheckCircle2,
		},
		{
			label: "Under Review",
			value: getUnderReviewCount(overallTotals),
			icon: CheckCircle2,
		},
		{
			label: "In Progress",
			value: overallTotals?.inProgressTasks ?? 0,
			icon: Clock3,
		},
		{
			label: "Hold Tasks",
			value: overallTotals?.holdTasks ?? 0,
			icon: PauseCircle,
		},
	];

	const totalCreated = getCreatedCount(overallTotals);
	const closureRate = totalCreated
		? `${Math.round((overallTotals.closedTasks / totalCreated) * 100)}%`
		: "0%";
	const pendingCount =
		(overallTotals?.openTasks ?? 0) + (overallTotals?.inProgressTasks ?? 0);

	const themeColors = isDark
		? {
				panel: "#1b1d21",
				panelAlt: "#20242a",
				border: "#32353b",
				text: "#f3f4f6",
				muted: "#9ca3af",
				heading: "#f9fafb",
				accent: "#60a5fa",
				accentSoft: "#24272d",
				iconBg: "#262a30",
				totalsBg: "#24272d",
				grid: "#32353b",
				tabBg: "#1b1d21",
				tabActive: "#2a2d33",
		  }
		: {
				panel: "#ffffff",
				panelAlt: "#ffffff",
				border: SURFACE_COLORS.border,
				text: "#111827",
				muted: "#6b7280",
				heading: "#374151",
				accent: SURFACE_COLORS.primary,
				accentSoft: SURFACE_COLORS.accentSoft,
				iconBg: SURFACE_COLORS.pale,
				totalsBg: SURFACE_COLORS.pale,
				grid: SURFACE_COLORS.border,
				tabBg: "#ffffff",
				tabActive: SURFACE_COLORS.soft,
		  };

	return (
		<>
			<div className="space-y-6">
				<div
					className="relative rounded-lg overflow-hidden shadow-md text-white mt-6"
					style={{
						backgroundImage: `url(${FrameBG})`,
						backgroundSize: "cover",
						backgroundPosition: "center",
					}}
				>
					<div className="p-8 flex flex-col gap-4 md:flex-row md:justify-between md:items-center">
						<div>
							<h2 className="text-xl font-semibold">Admin yearly insights</h2>
							<p className="text-sm opacity-90">
								Live overview of task performance and team delivery for{" "}
								{selectedYear}.
							</p>
						</div>
						<div className="flex items-center gap-3">
							<div
								className="flex rounded-lg border overflow-hidden bg-white"
								style={{
									borderColor: themeColors.border,
									backgroundColor: themeColors.tabBg,
								}}
							>
								<button
									type="button"
									onClick={() => setReportView("month")}
									className="px-4 py-2 text-sm font-medium transition-colors"
									style={{
										backgroundColor:
											reportView === "month"
												? themeColors.tabActive
												: "transparent",
										color:
											reportView === "month"
												? themeColors.accent
												: themeColors.muted,
									}}
								>
									Month Wise
								</button>
								<button
									type="button"
									onClick={() => setReportView("year")}
									className="px-4 py-2 text-sm font-medium transition-colors"
									style={{
										backgroundColor:
											reportView === "year"
												? themeColors.tabActive
												: "transparent",
										color:
											reportView === "year"
												? themeColors.accent
												: themeColors.muted,
									}}
								>
									Year Wise
								</button>
							</div>
							<div
								className="flex rounded-lg border overflow-hidden bg-white"
								style={{
									borderColor: themeColors.border,
									backgroundColor: themeColors.tabBg,
								}}
							>
								<button
									type="button"
									onClick={() => setReportScope("overall")}
									className="px-4 py-2 text-sm font-medium transition-colors"
									style={{
										backgroundColor:
											reportScope === "overall"
												? themeColors.tabActive
												: "transparent",
										color:
											reportScope === "overall"
												? themeColors.accent
												: themeColors.muted,
									}}
								>
									Overall
								</button>
								<button
									type="button"
									onClick={() => setReportScope("user")}
									className="px-4 py-2 text-sm font-medium transition-colors"
									style={{
										backgroundColor:
											reportScope === "user"
												? themeColors.tabActive
												: "transparent",
										color:
											reportScope === "user"
												? themeColors.accent
												: themeColors.muted,
									}}
								>
									User Wise
								</button>
							</div>
							<select
								value={selectedYear}
								onChange={(e) => setSelectedYear(e.target.value)}
								className="rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none border"
								style={{
									boxShadow: "none",
									borderColor: themeColors.border,
									backgroundColor: isDark ? "#1b1d21" : "#ffffff",
									color: isDark ? themeColors.text : "#374151",
								}}
							>
								{yearOptions.map((year) => (
									<option key={year} value={year}>
										{year}
									</option>
								))}
							</select>
							<button
								onClick={() => setShowModal(true)}
								className="bg-[#003a63] text-white px-5 py-2 rounded-lg"
							>
								Add Ticket
							</button>
						</div>
					</div>
				</div>

				<div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
					<div>
						<h3
							className="text-xl font-semibold text-gray-700"
							style={{ color: themeColors.heading }}
						>
							Insights
						</h3>
						<p className="text-sm text-gray-500" style={{ color: themeColors.muted }}>
							{reportView === "month"
								? reportScope === "overall"
									? "Month-wise task overview for the selected year"
									: "Month-wise task progress for the selected team member"
								: reportScope === "overall"
									? "Year-wise task summary for the selected year"
									: "Year-wise user performance summary for the selected year"}
						</p>
					</div>
					<div className="text-sm text-gray-500" style={{ color: themeColors.muted }}>
						{loading
							? "Refreshing report data..."
							: `${selectedYear} dashboard`}
					</div>
				</div>

				<div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
					{summaryCards.map((card) => {
						const Icon = card.icon;

						return (
							<div
								key={card.label}
								className="bg-white p-4 rounded-lg shadow flex justify-between items-center border"
								style={{
									borderColor: themeColors.border,
									backgroundColor: themeColors.panel,
								}}
							>
								<div>
									<p className="text-gray-500" style={{ color: themeColors.muted }}>
										{card.label}
									</p>
									<h2
										className="text-2xl font-bold mt-1"
										style={{ color: themeColors.accent }}
									>
										{card.value}
									</h2>
								</div>
								<div
									className="p-2 rounded-full"
									style={{
										backgroundColor: themeColors.iconBg,
										color: themeColors.accent,
									}}
								>
									<Icon className="w-8 h-8" />
								</div>
							</div>
						);
					})}
				</div>

				{reportView === "month" && reportScope === "overall" ? (
					<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
						<div
							className="bg-white p-4 rounded-lg shadow border"
							style={{
								borderColor: themeColors.border,
								backgroundColor: themeColors.panel,
							}}
						>
							<div className="flex justify-between items-center mb-3">
								<div>
									<h3 className="font-semibold">Monthly task trend</h3>
									<p className="text-sm text-gray-500" style={{ color: themeColors.muted }}>
										Track created, closed, under review and active tasks across each month
									</p>
								</div>
								<span
									className="text-sm font-medium px-3 py-1 rounded-full"
									style={{
										backgroundColor: themeColors.accentSoft,
										color: themeColors.accent,
									}}
								>
									Closure rate {closureRate}
								</span>
							</div>
							<div className="h-[280px]">
								{loading ? (
									<div className="h-full flex items-center justify-center text-gray-500" style={{ color: themeColors.muted }}>
										<Loader2 className="animate-spin mr-2" size={18} />
										Loading monthly report...
									</div>
								) : monthlyTrendData.length > 0 ? (
									<ResponsiveContainer width="100%" height="100%">
										<LineChart data={monthlyTrendData}>
											<CartesianGrid
												strokeDasharray="3 3"
												vertical={false}
												stroke={themeColors.grid}
											/>
											<XAxis dataKey="month" tick={{ fill: themeColors.muted }} />
											<YAxis allowDecimals={false} tick={{ fill: themeColors.muted }} />
											<Tooltip />
											<Legend />
											<Line
												type="monotone"
												dataKey="Created"
												stroke={STATUS_COLORS.totalTasks}
												strokeWidth={2.5}
											/>
											<Line
												type="monotone"
												dataKey="Closed"
												stroke={STATUS_COLORS.closedTasks}
												strokeWidth={2.5}
											/>
											<Line
												type="monotone"
												dataKey="Under Review"
												stroke={STATUS_COLORS.underReviewTasks}
												strokeWidth={2.5}
											/>
											<Line
												type="monotone"
												dataKey="Open"
												stroke={STATUS_COLORS.openTasks}
												strokeWidth={2.5}
											/>
											<Line
												type="monotone"
												dataKey="In Progress"
												stroke={SURFACE_COLORS.tealDark}
												strokeWidth={2.5}
											/>
											<Line
												type="monotone"
												dataKey="Hold"
												stroke={SURFACE_COLORS.soft}
												strokeWidth={2.5}
											/>
										</LineChart>
									</ResponsiveContainer>
								) : (
									<div className="h-full flex items-center justify-center text-gray-400" style={{ color: themeColors.muted }}>
										No monthly task data available
									</div>
								)}
							</div>
						</div>

						<div
							className="bg-white p-6 rounded-lg shadow-md flex flex-col border"
							style={{
								borderColor: themeColors.border,
								backgroundColor: themeColors.panel,
							}}
						>
							<div className="flex justify-between items-center mb-4">
								<div>
									<h3 className="font-semibold text-gray-700" style={{ color: themeColors.heading }}>
										Monthly status split
									</h3>
									<p className="text-sm text-gray-500" style={{ color: themeColors.muted }}>
										Overall status distribution for the selected year
									</p>
								</div>
								<span className="text-sm text-gray-500" style={{ color: themeColors.muted }}>
									{getCreatedCount(overallTotals)} created tasks
								</span>
							</div>

							<div className="flex flex-col md:flex-row items-center justify-center md:justify-between gap-6">
								<div className="relative w-full max-w-[420px] h-[280px]">
									{loading ? (
										<div className="h-full flex items-center justify-center text-gray-500">
											<Loader2 className="animate-spin mr-2" size={18} />
											Loading status split...
										</div>
									) : statusPieData.length > 0 ? (
										<>
											<ResponsiveContainer width="100%" height="100%">
												<PieChart>
													<Pie
														data={statusPieData}
														dataKey="value"
														nameKey="name"
														cx="50%"
														cy="50%"
														innerRadius={80}
														outerRadius={110}
														paddingAngle={3}
													>
														{statusPieData.map((entry) => (
															<Cell key={entry.name} fill={entry.color} />
														))}
													</Pie>
													<Tooltip />
												</PieChart>
											</ResponsiveContainer>
											<div className="absolute inset-0 flex flex-col items-center justify-center">
													<h2
														className="text-4xl font-bold"
														style={{ color: themeColors.accent }}
													>
													{closureRate}
												</h2>
												<p className="text-gray-500 text-sm mt-1 text-center" style={{ color: themeColors.muted }}>
													closed task rate
												</p>
											</div>
										</>
									) : (
										<div className="h-full flex items-center justify-center text-gray-400">
											No status data available
										</div>
									)}
								</div>

								<div className="w-full max-w-xs space-y-2">
									{statusPieData.map((item) => (
										<div
											key={item.name}
											className="flex items-center justify-between"
										>
											<div className="flex items-center space-x-2">
												<div
													className="w-3 h-3 rounded-sm"
													style={{ backgroundColor: item.color }}
												></div>
												<p className="text-gray-700 text-sm" style={{ color: themeColors.text }}>{item.name}</p>
											</div>
											<p className="text-sm font-medium text-gray-700" style={{ color: themeColors.text }}>
												{item.value}
											</p>
										</div>
									))}
									<div
										className="pt-3 mt-3 border-t text-sm text-gray-500"
										style={{ borderColor: themeColors.border, color: themeColors.muted }}
									>
										Top performer: {topPerformer?.userName || "N/A"}
									</div>
								</div>
							</div>
						</div>
					</div>
				) : reportView === "month" && reportScope === "user" ? (
					<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
						<div
							className="bg-white p-4 rounded-lg shadow border"
							style={{
								borderColor: themeColors.border,
								backgroundColor: themeColors.panel,
							}}
						>
							<div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between mb-3">
								<div>
									<h3 className="font-semibold" style={{ color: themeColors.heading }}>
										User monthly trend
									</h3>
									<p className="text-sm text-gray-500" style={{ color: themeColors.muted }}>
										Track month-wise task movement for the selected user
									</p>
								</div>
								<select
									value={selectedUserId}
									onChange={(e) => setSelectedUserId(e.target.value)}
									className="rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none border"
									style={{
										boxShadow: "none",
										borderColor: themeColors.border,
										backgroundColor: isDark ? "#1b1d21" : "#ffffff",
										color: isDark ? themeColors.text : "#374151",
									}}
								>
									{userOptions.map((user) => (
										<option key={user.userId} value={user.userId}>
											{user.userName}
										</option>
									))}
								</select>
							</div>
							<div className="h-[280px]">
								{loading ? (
									<div className="h-full flex items-center justify-center text-gray-500" style={{ color: themeColors.muted }}>
										<Loader2 className="animate-spin mr-2" size={18} />
										Loading user monthly report...
									</div>
								) : selectedUserMonthlyChartData.length > 0 ? (
									<ResponsiveContainer width="100%" height="100%">
										<LineChart data={selectedUserMonthlyChartData}>
											<CartesianGrid
												strokeDasharray="3 3"
												vertical={false}
												stroke={themeColors.grid}
											/>
											<XAxis dataKey="month" tick={{ fill: themeColors.muted }} />
											<YAxis allowDecimals={false} tick={{ fill: themeColors.muted }} />
											<Tooltip />
											<Legend />
											<Line
												type="monotone"
												dataKey="Created"
												stroke={STATUS_COLORS.totalTasks}
												strokeWidth={2.5}
											/>
											<Line
												type="monotone"
												dataKey="Closed"
												stroke={STATUS_COLORS.closedTasks}
												strokeWidth={2.5}
											/>
											<Line
												type="monotone"
												dataKey="Under Review"
												stroke={STATUS_COLORS.underReviewTasks}
												strokeWidth={2.5}
											/>
											<Line
												type="monotone"
												dataKey="Open"
												stroke={STATUS_COLORS.openTasks}
												strokeWidth={2.5}
											/>
											<Line
												type="monotone"
												dataKey="In Progress"
												stroke={SURFACE_COLORS.tealDark}
												strokeWidth={2.5}
											/>
											<Line
												type="monotone"
												dataKey="Hold"
												stroke={SURFACE_COLORS.soft}
												strokeWidth={2.5}
											/>
										</LineChart>
									</ResponsiveContainer>
								) : (
									<div className="h-full flex items-center justify-center text-gray-400" style={{ color: themeColors.muted }}>
										No user monthly data available
									</div>
								)}
							</div>
						</div>

						<div
							className="bg-white p-4 rounded-lg shadow border"
							style={{
								borderColor: themeColors.border,
								backgroundColor: themeColors.panel,
							}}
						>
							<div className="flex items-center justify-between mb-3">
								<div>
									<h3 className="font-semibold" style={{ color: themeColors.heading }}>
										User monthly breakdown
									</h3>
									<p className="text-sm text-gray-500" style={{ color: themeColors.muted }}>
										Monthly task summary for the selected user
									</p>
								</div>
								<span
									className="text-sm font-medium px-3 py-1 rounded-full"
									style={{
										backgroundColor: themeColors.accentSoft,
										color: themeColors.accent,
									}}
								>
									{getCreatedCount(selectedUserTotals)} created tasks
								</span>
							</div>
							<div className="overflow-x-auto">
								<table className="min-w-full text-sm">
									<thead>
										<tr
											className="border-b text-left text-gray-500"
											style={{ borderColor: themeColors.border, color: themeColors.muted }}
										>
											<th className="py-3 pr-4 font-medium">Month</th>
											<th className="py-3 px-4 font-medium">Created</th>
											<th className="py-3 px-4 font-medium">Closed</th>
											<th className="py-3 px-4 font-medium">Under Review</th>
											<th className="py-3 px-4 font-medium">Hold</th>
											<th className="py-3 px-4 font-medium">Open</th>
											<th className="py-3 px-4 font-medium">In Progress</th>
										</tr>
									</thead>
									<tbody>
										{selectedUserMonthlyRows.map((row) => (
											<tr
												key={`${row.userId}-${row.monthNo}`}
												className="border-b last:border-b-0"
												style={{ borderColor: themeColors.border, color: themeColors.text }}
											>
												<td className="py-3 pr-4 font-medium text-gray-700" style={{ color: themeColors.heading }}>
													{row.monthName}
												</td>
												<td className="py-3 px-4">{getCreatedCount(row)}</td>
												<td className="py-3 px-4">{row.closedTasks}</td>
												<td className="py-3 px-4">{getUnderReviewCount(row)}</td>
												<td className="py-3 px-4">{row.holdTasks}</td>
												<td className="py-3 px-4">{row.openTasks}</td>
												<td className="py-3 px-4">{row.inProgressTasks}</td>
											</tr>
										))}
										{selectedUserTotals && (
											<tr
												className="font-semibold"
												style={{ backgroundColor: themeColors.totalsBg, color: themeColors.text }}
											>
												<td className="py-3 pr-4">
													{selectedUserTotals.monthName}
												</td>
												<td className="py-3 px-4">
													{getCreatedCount(selectedUserTotals)}
												</td>
												<td className="py-3 px-4">
													{selectedUserTotals.closedTasks}
												</td>
												<td className="py-3 px-4">
													{getUnderReviewCount(selectedUserTotals)}
												</td>
												<td className="py-3 px-4">
													{selectedUserTotals.holdTasks}
												</td>
												<td className="py-3 px-4">
													{selectedUserTotals.openTasks}
												</td>
												<td className="py-3 px-4">
													{selectedUserTotals.inProgressTasks}
												</td>
											</tr>
										)}
									</tbody>
								</table>
							</div>
						</div>
					</div>
				) : reportScope === "user" ? (
					<div className="space-y-6">
						<div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
							<div
								className="bg-white p-4 rounded-lg shadow border"
								style={{ borderColor: themeColors.border, backgroundColor: themeColors.panel }}
							>
								<p className="text-gray-500 text-sm" style={{ color: themeColors.muted }}>Active Users</p>
								<p
									className="text-2xl font-bold mt-1"
									style={{ color: themeColors.accent }}
								>
									{userTotals.length}
								</p>
							</div>
							<div
								className="bg-white p-4 rounded-lg shadow border"
								style={{ borderColor: themeColors.border, backgroundColor: themeColors.panel }}
							>
								<p className="text-gray-500 text-sm" style={{ color: themeColors.muted }}>Top Contributor</p>
								<p
									className="text-lg font-bold mt-1 truncate"
									style={{ color: themeColors.accent }}
								>
									{userTotals[0]?.userName || "N/A"}
								</p>
							</div>
							<div
								className="bg-white p-4 rounded-lg shadow border"
								style={{ borderColor: themeColors.border, backgroundColor: themeColors.panel }}
							>
								<p className="text-gray-500 text-sm" style={{ color: themeColors.muted }}>Highest Created</p>
								<p
									className="text-2xl font-bold mt-1"
									style={{ color: themeColors.accent }}
								>
									{getCreatedCount(userTotals[0])}
								</p>
							</div>
							<div
								className="bg-white p-4 rounded-lg shadow border"
								style={{ borderColor: themeColors.border, backgroundColor: themeColors.panel }}
							>
								<p className="text-gray-500 text-sm" style={{ color: themeColors.muted }}>Best Closure</p>
								<p
									className="text-2xl font-bold mt-1"
									style={{ color: themeColors.accent }}
								>
									{getCreatedCount(topPerformer)
										? `${Math.round((topPerformer.closedTasks / getCreatedCount(topPerformer)) * 100)}%`
										: "0%"}
								</p>
							</div>
						</div>

						<div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
							<div
								className="xl:col-span-2 bg-white p-4 rounded-lg shadow border"
								style={{ borderColor: themeColors.border, backgroundColor: themeColors.panel }}
							>
								<div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between mb-4">
									<div>
										<h3 className="font-semibold">
											Year-wise user performance
										</h3>
										<p className="text-sm text-gray-500" style={{ color: themeColors.muted }}>
											Compare created, closed, under review and open work across the team
										</p>
									</div>
									<span
										className="text-sm font-medium px-3 py-1 rounded-full"
										style={{
											backgroundColor: themeColors.accentSoft,
											color: themeColors.accent,
										}}
									>
										Top 6 users
									</span>
								</div>

								<div className="h-[360px]">
									{loading ? (
										<div className="h-full flex items-center justify-center text-gray-500" style={{ color: themeColors.muted }}>
											<Loader2 className="animate-spin mr-2" size={18} />
											Loading year-wise report...
										</div>
									) : userChartData.length > 0 ? (
										<ResponsiveContainer width="100%" height="100%">
											<BarChart data={userChartData} barGap={10}>
												<CartesianGrid
													strokeDasharray="3 3"
													vertical={false}
													stroke={themeColors.grid}
												/>
												<XAxis dataKey="name" tick={{ fill: themeColors.muted }} />
												<YAxis allowDecimals={false} tick={{ fill: themeColors.muted }} />
												<Tooltip />
												<Legend />
												<Bar
													dataKey="Created"
													fill={SURFACE_COLORS.soft}
													radius={[6, 6, 0, 0]}
												/>
												<Bar
													dataKey="Closed"
													fill={SURFACE_COLORS.primary}
													radius={[6, 6, 0, 0]}
												/>
												<Bar
													dataKey="Under Review"
													fill={STATUS_COLORS.underReviewTasks}
													radius={[6, 6, 0, 0]}
												/>
												<Bar
													dataKey="Open"
													fill={SURFACE_COLORS.accent}
													radius={[6, 6, 0, 0]}
												/>
											</BarChart>
										</ResponsiveContainer>
									) : (
										<div className="h-full flex items-center justify-center text-gray-400" style={{ color: themeColors.muted }}>
											No year-wise task data available
										</div>
									)}
								</div>
							</div>

							<div
								className="bg-white p-4 rounded-lg shadow border"
								style={{ borderColor: themeColors.border, backgroundColor: themeColors.panel }}
							>
								<h3 className="font-semibold mb-1" style={{ color: themeColors.heading }}>Top Performers</h3>
								<p className="text-sm text-gray-500 mb-4" style={{ color: themeColors.muted }}>
									Quick view of the strongest yearly contributors
								</p>
								<div className="space-y-3">
									{userTotals.slice(0, 5).map((row, index) => {
										const createdCount = getCreatedCount(row);
										const closure = createdCount
											? Math.round((row.closedTasks / createdCount) * 100)
											: 0;

										return (
											<div
												key={row.userId}
												className="rounded-lg border p-3"
												style={{
													borderColor: themeColors.border,
													backgroundColor:
														index === 0 ? themeColors.accentSoft : themeColors.panelAlt,
												}}
											>
												<div className="flex items-center justify-between gap-3">
													<div>
														<p className="font-medium text-gray-800" style={{ color: themeColors.text }}>
															{row.userName}
														</p>
														<p className="text-xs text-gray-500" style={{ color: themeColors.muted }}>
															Created {getCreatedCount(row)} • Closed{" "}
															{row.closedTasks}
														</p>
													</div>
													<div className="text-right">
														<p
															className="text-sm font-semibold"
															style={{ color: themeColors.accent }}
														>
															{closure}%
														</p>
														<p className="text-xs text-gray-500" style={{ color: themeColors.muted }}>closure</p>
													</div>
												</div>
											</div>
										);
									})}
								</div>
							</div>
						</div>

						<div
							className="bg-white p-4 rounded-lg shadow border"
							style={{ borderColor: themeColors.border, backgroundColor: themeColors.panel }}
						>
							<div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between mb-3">
								<div>
									<h3 className="font-semibold">User-wise yearly breakdown</h3>
									<p className="text-sm text-gray-500" style={{ color: themeColors.muted }}>
										Detailed yearly counts for each user
									</p>
								</div>
								<span className="text-sm text-gray-500" style={{ color: themeColors.muted }}>
									{userTotals.length} users with report data
								</span>
							</div>

							<div className="overflow-x-auto">
								<table className="min-w-full text-sm">
									<thead>
										<tr
											className="border-b text-left text-gray-500"
											style={{ borderColor: themeColors.border, color: themeColors.muted }}
										>
											<th className="py-3 pr-4 font-medium">User</th>
											<th className="py-3 px-4 font-medium">Created</th>
											<th className="py-3 px-4 font-medium">Closed</th>
											<th className="py-3 px-4 font-medium">Under Review</th>
											<th className="py-3 px-4 font-medium">Hold</th>
											<th className="py-3 px-4 font-medium">Open</th>
											<th className="py-3 px-4 font-medium">In Progress</th>
										</tr>
									</thead>
									<tbody>
										{userTotals.map((row) => (
											<tr
												key={row.userId}
												className="border-b last:border-b-0"
												style={{ borderColor: themeColors.border, color: themeColors.text }}
											>
												<td className="py-3 pr-4 font-medium text-gray-700" style={{ color: themeColors.heading }}>
													{row.userName}
												</td>
												<td className="py-3 px-4">{getCreatedCount(row)}</td>
												<td className="py-3 px-4">{row.closedTasks}</td>
												<td className="py-3 px-4">{getUnderReviewCount(row)}</td>
												<td className="py-3 px-4">{row.holdTasks}</td>
												<td className="py-3 px-4">{row.openTasks}</td>
												<td className="py-3 px-4">{row.inProgressTasks}</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						</div>
					</div>
				) : (
					<div className="space-y-6">
						<div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
							<div
								className="bg-white p-4 rounded-lg shadow border"
								style={{ borderColor: themeColors.border, backgroundColor: themeColors.panel }}
							>
								<p className="text-gray-500 text-sm" style={{ color: themeColors.muted }}>Created</p>
								<p
									className="text-2xl font-bold mt-1"
									style={{ color: themeColors.accent }}
								>
									{getCreatedCount(overallTotals)}
								</p>
							</div>
							<div
								className="bg-white p-4 rounded-lg shadow border"
								style={{ borderColor: themeColors.border, backgroundColor: themeColors.panel }}
							>
								<p className="text-gray-500 text-sm" style={{ color: themeColors.muted }}>Closed</p>
								<p
									className="text-2xl font-bold mt-1"
									style={{ color: themeColors.accent }}
								>
									{overallTotals?.closedTasks ?? 0}
								</p>
							</div>
							<div
								className="bg-white p-4 rounded-lg shadow border"
								style={{ borderColor: themeColors.border, backgroundColor: themeColors.panel }}
							>
								<p className="text-gray-500 text-sm" style={{ color: themeColors.muted }}>Under Review</p>
								<p
									className="text-2xl font-bold mt-1"
									style={{ color: themeColors.accent }}
								>
									{getUnderReviewCount(overallTotals)}
								</p>
							</div>
							<div
								className="bg-white p-4 rounded-lg shadow border"
								style={{ borderColor: themeColors.border, backgroundColor: themeColors.panel }}
							>
								<p className="text-gray-500 text-sm" style={{ color: themeColors.muted }}>Pending</p>
								<p
									className="text-2xl font-bold mt-1"
									style={{ color: themeColors.accent }}
								>
									{pendingCount}
								</p>
							</div>
							<div
								className="bg-white p-4 rounded-lg shadow border"
								style={{ borderColor: themeColors.border, backgroundColor: themeColors.panel }}
							>
								<p className="text-gray-500 text-sm" style={{ color: themeColors.muted }}>Closure Rate</p>
								<p
									className="text-2xl font-bold mt-1"
									style={{ color: themeColors.accent }}
								>
									{closureRate}
								</p>
							</div>
						</div>

						<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
							<div
								className="bg-white p-4 rounded-lg shadow border"
								style={{ borderColor: themeColors.border, backgroundColor: themeColors.panel }}
							>
								<div className="flex justify-between items-center mb-3">
									<div>
										<h3 className="font-semibold">Year summary</h3>
										<p className="text-sm text-gray-500" style={{ color: themeColors.muted }}>
											High-level yearly status distribution
										</p>
									</div>
									<span
										className="text-sm font-medium px-3 py-1 rounded-full"
										style={{
											backgroundColor: themeColors.accentSoft,
											color: themeColors.accent,
										}}
									>
										Closure rate {closureRate}
									</span>
								</div>
								<div className="h-[320px]">
									{loading ? (
										<div className="h-full flex items-center justify-center text-gray-500" style={{ color: themeColors.muted }}>
											<Loader2 className="animate-spin mr-2" size={18} />
											Loading year summary...
										</div>
									) : statusPieData.length > 0 ? (
										<ResponsiveContainer width="100%" height="100%">
											<BarChart
												data={[
													{
														name: "Tasks",
														Created: getCreatedCount(overallTotals),
														Closed: overallTotals?.closedTasks ?? 0,
														"Under Review": getUnderReviewCount(overallTotals),
														Open: overallTotals?.openTasks ?? 0,
														"In Progress": overallTotals?.inProgressTasks ?? 0,
														Hold: overallTotals?.holdTasks ?? 0,
													},
												]}
											>
												<CartesianGrid
													strokeDasharray="3 3"
													vertical={false}
													stroke={themeColors.grid}
												/>
												<XAxis dataKey="name" tick={{ fill: themeColors.muted }} />
												<YAxis allowDecimals={false} tick={{ fill: themeColors.muted }} />
												<Tooltip />
												<Legend />
												<Bar
													dataKey="Created"
													fill={STATUS_COLORS.totalTasks}
													radius={[6, 6, 0, 0]}
												/>
												<Bar
													dataKey="Closed"
													fill={STATUS_COLORS.closedTasks}
													radius={[6, 6, 0, 0]}
												/>
												<Bar
													dataKey="Under Review"
													fill={STATUS_COLORS.underReviewTasks}
													radius={[6, 6, 0, 0]}
												/>
												<Bar
													dataKey="Open"
													fill={SURFACE_COLORS.accent}
													radius={[6, 6, 0, 0]}
												/>
												<Bar
													dataKey="In Progress"
													fill={SURFACE_COLORS.tealDark}
													radius={[6, 6, 0, 0]}
												/>
												<Bar
													dataKey="Hold"
													fill={SURFACE_COLORS.soft}
													radius={[6, 6, 0, 0]}
												/>
											</BarChart>
										</ResponsiveContainer>
									) : (
										<div className="h-full flex items-center justify-center text-gray-400" style={{ color: themeColors.muted }}>
											No year summary available
										</div>
									)}
								</div>
							</div>

							<div
								className="bg-white p-5 rounded-lg shadow border"
								style={{ borderColor: themeColors.border, backgroundColor: themeColors.panel }}
							>
								<div className="flex items-center justify-between mb-4">
									<div>
										<h3 className="font-semibold">Year overview</h3>
										<p className="text-sm text-gray-500" style={{ color: themeColors.muted }}>
											Readable snapshot of created and delivery status
										</p>
									</div>
									<span
										className="text-sm font-medium px-3 py-1 rounded-full"
										style={{
											backgroundColor: themeColors.accentSoft,
											color: themeColors.accent,
										}}
									>
										{getCreatedCount(overallTotals)} created
									</span>
								</div>
								<div className="grid grid-cols-2 gap-3">
									<div
										className="rounded-lg p-4"
										style={{ backgroundColor: themeColors.panelAlt, border: `1px solid ${themeColors.border}` }}
									>
										<p className="text-xs text-gray-500" style={{ color: themeColors.muted }}>Created</p>
										<p
											className="text-xl font-bold mt-1"
											style={{ color: themeColors.accent }}
										>
											{getCreatedCount(overallTotals)}
										</p>
									</div>
									<div
										className="rounded-lg p-4"
										style={{ backgroundColor: themeColors.panelAlt, border: `1px solid ${themeColors.border}` }}
									>
										<p className="text-xs text-gray-500" style={{ color: themeColors.muted }}>Closed</p>
										<p
											className="text-xl font-bold mt-1"
											style={{ color: themeColors.accent }}
										>
											{overallTotals?.closedTasks ?? 0}
										</p>
									</div>
									<div
										className="rounded-lg p-4"
										style={{ backgroundColor: themeColors.panelAlt, border: `1px solid ${themeColors.border}` }}
									>
										<p className="text-xs text-gray-500" style={{ color: themeColors.muted }}>Under Review</p>
										<p
											className="text-xl font-bold mt-1"
											style={{ color: themeColors.accent }}
										>
											{getUnderReviewCount(overallTotals)}
										</p>
									</div>
									<div
										className="rounded-lg p-4"
										style={{ backgroundColor: themeColors.panelAlt, border: `1px solid ${themeColors.border}` }}
									>
										<p className="text-xs text-gray-500" style={{ color: themeColors.muted }}>Open</p>
										<p
											className="text-xl font-bold mt-1"
											style={{ color: themeColors.accent }}
										>
											{overallTotals?.openTasks ?? 0}
										</p>
									</div>
									<div
										className="rounded-lg p-4"
										style={{ backgroundColor: themeColors.panelAlt, border: `1px solid ${themeColors.border}` }}
									>
										<p className="text-xs text-gray-500" style={{ color: themeColors.muted }}>In Progress</p>
										<p
											className="text-xl font-bold mt-1"
											style={{ color: themeColors.accent }}
										>
											{overallTotals?.inProgressTasks ?? 0}
										</p>
									</div>
									<div
										className="rounded-lg p-4 col-span-2"
										style={{ backgroundColor: themeColors.panelAlt, border: `1px solid ${themeColors.border}` }}
									>
										<div className="flex items-center justify-between">
											<div>
												<p className="text-xs text-gray-500" style={{ color: themeColors.muted }}>On Hold</p>
												<p
													className="text-xl font-bold mt-1"
													style={{ color: themeColors.accent }}
												>
													{overallTotals?.holdTasks ?? 0}
												</p>
											</div>
											<div className="text-right">
												<p className="text-xs text-gray-500" style={{ color: themeColors.muted }}>Pending work</p>
												<p
													className="text-xl font-bold mt-1"
													style={{ color: themeColors.accent }}
												>
													{pendingCount}
												</p>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				)}

				{error && (
					<div
						className="text-sm font-medium"
						style={{ color: isDark ? "#fda4af" : SURFACE_COLORS.secondary }}
					>
						{error}
					</div>
				)}
			</div>

			<CreateTicketModal
				show={showModal}
				onClose={() => setShowModal(false)}
				onCreated={() => {}}
			/>
		</>
	);
};

export default AdminHome;
